import React, { useEffect, useRef, useState } from "react";
import "./OTP.css";
import AuthLayout from "../AuthLayout/AuthLayout";
import { Button, message } from "antd";
import { Redirect, useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { requestPasswordResetOtp, verifyPasswordResetOtp } from "../../api/orgRegistry";
import {
  readPasswordResetFlow,
  canVerifyOtp,
  savePasswordResetSession,
  otpExpiryFromNow,
  otpSecondsRemaining,
} from "../../utils/passwordResetSession";

const OTP_LENGTH = 6;

// "dulana@ecologital.com" -> "du*****@ecologital.com"
const maskEmail = (email) => {
  if (!email || !email.includes("@")) return email || "";
  const [name, domain] = email.split("@");
  const visible = name.slice(0, 2);
  return `${visible}${"*".repeat(Math.max(name.length - visible.length, 1))}@${domain}`;
};

const emptyOtp = () => new Array(OTP_LENGTH).fill("");

const OTPVerify = () => {
  const [otp, setOtp] = useState(emptyOtp);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const location = useLocation();
  const history = useHistory();
  const inputsRef = useRef(null);

  /* Router state on normal navigation, the session store on refresh. Read once into
     state so a re-render cannot swap the request id out from under an in-flight verify. */
  const [flow, setFlow] = useState(() => readPasswordResetFlow(location.state));

  const [secondsLeft, setSecondsLeft] = useState(() => otpSecondsRemaining(flow.otpExpiresAt));

  /* Ticks off the absolute expiry rather than counting down a number, so the value is
     right after a refresh and does not drift while the tab is backgrounded. */
  useEffect(() => {
    setSecondsLeft(otpSecondsRemaining(flow.otpExpiresAt));

    const intervalId = setInterval(() => {
      const remaining = otpSecondsRemaining(flow.otpExpiresAt);
      setSecondsLeft(remaining);
      if (remaining <= 0) clearInterval(intervalId);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [flow.otpExpiresAt]);

  const countdownFinished = secondsLeft <= 0;
  const enteredOtp = otp.join("");
  const isOtpComplete = enteredOtp.length === OTP_LENGTH;

  const focusField = (index) => {
    const fields = inputsRef.current?.querySelectorAll(".otp-field");
    fields?.[index]?.focus();
  };

  const handleChange = (element, index) => {
    const digit = element.value.replace(/\D/g, "").slice(-1);
    // a cleared box must stay cleared, so an empty value is a valid change
    if (element.value !== "" && digit === "") return;

    setOtp(otp.map((d, idx) => (idx === index ? digit : d)));
    if (digit && index < OTP_LENGTH - 1) focusField(index + 1);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    // codes get pasted out of an email with spaces or a stray line break attached
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!digits) return;

    const updated = emptyOtp();
    digits.split("").forEach((d, i) => {
      updated[i] = d;
    });
    setOtp(updated);
    focusField(Math.min(digits.length, OTP_LENGTH - 1));
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      e.preventDefault();
      setOtp(otp.map((d, idx) => (idx === index - 1 ? "" : d)));
      focusField(index - 1);
    }
    if (e.key === "ArrowLeft" && index > 0) focusField(index - 1);
    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) focusField(index + 1);
  };

  const resetFields = () => {
    setOtp(emptyOtp());
    focusField(0);
  };

  const handleVerify = async () => {
    if (!isOtpComplete || isLoading) return;
    setIsLoading(true);

    try {
      await verifyPasswordResetOtp(flow.otpRequestId, enteredOtp);

      /* Step 3 re-sends the code with the new password, so it travels on. `replace`,
         not `push`: once a code is spent, Back must not land on this screen again. */
      savePasswordResetSession({ otp: enteredOtp, otpVerified: true });
      // settled before navigating away, so this screen is not written to after unmount
      setIsLoading(false);
      history.replace("/create-new-password", {
        email: flow.email,
        otpRequestId: flow.otpRequestId,
        otp: enteredOtp,
        otpVerified: true,
      });
    } catch (error) {
      console.error("OTP verification failed:", error);
      message.error(error.message || "We couldn't verify that code. Please try again.");
      setIsLoading(false);
      resetFields();
    }
  };

  const handleResend = async () => {
    if (isResending || isLoading) return;
    setIsResending(true);

    try {
      // a resend issues a fresh code, so the previous request id is dead
      const otpRequestId = await requestPasswordResetOtp(flow.email);
      const otpExpiresAt = otpExpiryFromNow();

      savePasswordResetSession({ otpRequestId, otpExpiresAt, otp: "", otpVerified: false });
      setFlow((prev) => ({ ...prev, otpRequestId, otpExpiresAt }));
      resetFields();
      message.success("We've sent a new verification code to your email.");
    } catch (error) {
      console.error("Resending the OTP failed:", error);
      message.error(error.message || "We couldn't resend the code. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  /* No request id means step 1 never ran (direct URL, or a tab left open past its
     session). Already verified means this is a Back into a spent step - both restart
     the flow, and step 1 clears the leftover state as it mounts. */
  if (!canVerifyOtp(flow)) {
    return <Redirect to="/forget-password" />;
  }

  return (
    <AuthLayout>
      <div className="otp-verify-container">
        <h1>OTP Verification</h1>
        <p>Enter the verification code that has been sent to your email</p>
        <span>{maskEmail(flow.email)}</span>
      </div>
      <div className="otp-container" ref={inputsRef}>
        {otp.map((data, index) => (
          <input
            className="otp-field"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            name="otp"
            maxLength="1"
            placeholder=""
            key={index}
            value={data}
            disabled={isLoading}
            onChange={(e) => handleChange(e.target, index)}
            onFocus={(e) => e.target.select()}
            onPaste={handlePaste}
            onKeyDown={(e) => handleKeyDown(e, index)}
          />
        ))}
      </div>
      <div className="otp-verify-footer">
        {/* The countdown is a hint, not a gate. Emails arrive late, and the service is
            the only thing that actually knows whether a code is still good - so the
            fields stay usable after it reaches zero and a late code still verifies. */}
        {countdownFinished ? (
          <p>Your code may have expired. Try it anyway, or resend to get a new one.</p>
        ) : (
          <p>
            OTP Expires in <span id="timer">({secondsLeft})</span> seconds
          </p>
        )}
        <p className="resend-otp">
          Haven't received the OTP code ?{" "}
          <span id="resend" onClick={handleResend}>
            {isResending ? "Sending..." : "Resend"}
          </span>
        </p>
        <Button
          htmlType="submit"
          type="primary"
          className="signin-btn"
          onClick={handleVerify}
          disabled={!isOtpComplete || isLoading}
        >
          {isLoading ? "Verifying..." : "Verify"}
        </Button>
      </div>
    </AuthLayout>
  );
};

export default OTPVerify;
